<script setup lang="ts">
import { z } from 'zod'

definePageMeta({ layout: 'auth', middleware: ['tenant', 'guest'] })

const { t } = useI18n()
const localePath = useLocalePath()
const route = useRoute()
const auth = useAuth()
const member = useMember()
const referralCookie = useCookie<string | null>('referral-code', {
  sameSite: 'lax',
  secure: import.meta.env.PROD
})

const schema = z.object({
  identifier: z.string().email(),
  password: z.string().min(6)
})

type Schema = {
  identifier: string
  password: string
}

const tenant = useTenant()

const state = reactive<Schema>({
  identifier: '',
  password: ''
})

const error = ref<string | null>(null)
const showPassword = ref(false)
const remember = ref(false)
const redirectPath = computed(() => {
  const raw = typeof route.query.redirect === 'string' ? route.query.redirect : null
  return auth.getPostLoginRedirect(raw)
})

const redirectQuery = computed<Record<string, string>>(() => {
  const keys = ['mentor', 'mentorName', 'date', 'time']
  const next: Record<string, string> = {}

  for (const key of keys) {
    const value = route.query[key]
    if (typeof value === 'string' && value.length) {
      next[key] = value
    }
  }

  return next
})

const pendingBookingText = computed(() => {
  const mentorName = redirectQuery.value.mentorName
  const date = redirectQuery.value.date
  const time = redirectQuery.value.time

  if (!mentorName || !date || !time) {
    return null
  }

  return `Selected session: ${mentorName} on ${date} at ${time}`
})

const referralFromQuery = computed(() => {
  const keys = ['referral_code', 'referral', 'ref', 'invite']

  for (const key of keys) {
    const value = route.query[key]
    if (typeof value === 'string' && value.trim().length) {
      return value.trim()
    }
  }

  return null
})


watchEffect(() => {
  if (referralFromQuery.value) {
    referralCookie.value = referralFromQuery.value
  }
})

const oauthError = computed(() => {
  return route.query.error === 'google_failed'
    ? t('auth.google_failed_error')
    : null
})

const onSubmit = async () => {
  error.value = null

  try {
    const parsed = schema.parse(state)
    const result = await auth.login({
      email: parsed.identifier,
      password: parsed.password,
      remember: remember.value
    })

    if (result?.requiresEmailVerification) {
      await navigateTo(localePath('/verify-otp'))
      return
    }

    if (!auth.user.value?.timezone) {
      try {
        const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone
        if (browserTz) {
          await member.update_profile({ timezone: browserTz })
        }
      } catch {
        // ignore — non-critical
      }
    }

    // Make sure the tenant membership is loaded before computing the
    // redirect path. Without this, fresh logins on a tenant subdomain
    // always fall through to /me because tenant.membership is null at
    // the moment getPostLoginRedirect() reads it. redirectPath is a
    // computed, so it re-evaluates as soon as membership lands.
    if (tenant.tenantMode.value !== 'platform') {
      try { await tenant.checkMembership() } catch { /* non-fatal */ }
    }

    await navigateTo({
      path: redirectPath.value,
      query: Object.keys(redirectQuery.value).length ? redirectQuery.value : undefined
    })
  } catch (e) {
    if (e instanceof z.ZodError) {
      error.value = t('auth.invalid_email_error')
      return
    }

    const statusCode = (e as { statusCode?: number })?.statusCode
    if (statusCode === 429) {
      error.value = t('auth.too_many_requests_error')
      return
    }

    const msg = e instanceof Error ? e.message : ''
    error.value = msg || t('auth.login_failed_error')
  }
}

const onGoogleLogin = async () => {
  const referral = referralFromQuery.value || referralCookie.value || null
  await auth.googleLogin(referral)
}
</script>

<template>
  <section class="flex min-h-screen w-full">
    <div class="relative hidden w-2/3 items-center justify-center p-10 lg:flex">
      <img
        src="/images/login-avatar.png"
        alt="Login illustration"
        class="max-h-[500px] w-auto"
      >
    </div>

    <div class="flex w-full flex-col justify-center px-6 py-12 lg:w-1/3 lg:px-12 ui-bg-elevated">
      <div class="mx-auto w-full max-w-sm">
        <h4 class="text-2xl font-semibold">
          <span>Zero To Hero тавтай морил!</span> 👋🏻
        </h4>

        <UAlert
          v-if="oauthError"
          color="error"
          variant="soft"
          :description="oauthError"
          class="mt-4"
        />

        <UAlert
          v-if="pendingBookingText"
          color="primary"
          variant="soft"
          :description="pendingBookingText"
          class="mt-4"
        />

        <UAlert
          v-if="error"
          color="error"
          variant="soft"
          :description="error"
          class="mt-4"
        />

        <form
          class="mt-6 space-y-4"
          @submit.prevent="onSubmit"
        >
          <UFormField
            label="Email"
            for="identifier"
          >
            <UInput
              id="identifier"
              v-model="state.identifier"
              placeholder="johndoe@email.com"
              type="email"
              autofocus
              class="w-full"
            />
          </UFormField>

          <UFormField
            label="Password"
            for="password"
          >
            <UInput
              id="password"
              v-model="state.password"
              :type="showPassword ? 'text' : 'password'"
              placeholder="············"
              class="w-full"
            >
              <template #trailing>
                <button
                  type="button"
                  class="ui-text-subtle hover:text-primary"
                  @click="showPassword = !showPassword"
                >
                  <UIcon
                    :name="showPassword ? 'i-lucide-eye-off' : 'i-lucide-eye'"
                    class="size-4"
                  />
                </button>
              </template>
            </UInput>
          </UFormField>

          <div class="flex items-center justify-between">
            <UCheckbox
              v-model="remember"
              label="Сануулах"
            />
            <NuxtLink
              :to="localePath('/forgot-password')"
              class="text-sm font-medium text-primary hover:underline"
            >
              Нууц үг мартсан уу?
            </NuxtLink>
          </div>

          <UButton
            type="submit"
            :loading="auth.isLoading.value"
            class="w-full justify-center"
          >
            Нэвтрэх
          </UButton>
        </form>

        <div class="relative my-5 flex items-center">
          <div class="flex-1 border-t ui-border-base" />
          <span class="px-3 text-sm ui-text-subtle">эсвэл</span>
          <div class="flex-1 border-t ui-border-base" />
        </div>

        <UButton
          variant="outline"
          color="neutral"
          class="w-full justify-center gap-2"
          :loading="auth.isLoading.value"
          @click="onGoogleLogin"
        >
          <img
            src="/images/icons/google-g.svg"
            alt="Google"
            class="size-4"
          >
          Login with Google
        </UButton>

      </div>
    </div>
  </section>
</template>
